/**
 * Dashboard = RINGKASAN saja.
 * Detail market / kalender / sentimen / likuidasi → halaman sidebar terpisah.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Candidate, type MarketContext, type CycleSummary, getOptionLabel, getOptionIcon } from '../lib/api';
import { fmt } from '../lib/format';
import { brand, desktop } from '../brand';

export function DashboardPage() {
  const qc = useQueryClient();

  const { data: candidatesData, isLoading: candLoading } = useQuery({
    queryKey: ['candidates', 'latest-cycle'],
    queryFn: () =>
      api.get<{
        candidates: Candidate[];
        count: number;
        cycle_id?: string | null;
        stats?: { total: number; passed: number; rejected: number };
      }>('/api/bot/candidates?limit=400&cycle_id=latest'),
    refetchInterval: 30_000,
  });

  const { data: ctxBtc } = useQuery({
    queryKey: ['context', 'BTCUSDT'],
    queryFn: () => api.get<MarketContext>('/api/data/context?symbol=BTCUSDT'),
    refetchInterval: 60_000,
  });

  const { data: activityWrap } = useQuery({
    queryKey: ['bot-activity'],
    queryFn: () =>
      api.get<{
        activity: {
          running: boolean;
          cycle_id: string | null;
          steps: Array<{ at: string; level: string; message: string }>;
          result: any;
        };
      }>('/api/bot/activity'),
    refetchInterval: (q) => ((q.state.data as any)?.activity?.running ? 1000 : 8000),
  });
  const activity = activityWrap?.activity;

  // Cycle summary — ambil dari cycle_id candidates terakhir
  const cycleId = candidatesData?.cycle_id;
  const { data: cycleSummary } = useQuery({
    queryKey: ['cycle-summary', cycleId],
    queryFn: () => api.get<CycleSummary>(`/api/bot/cycle/${cycleId}/summary`),
    enabled: !!cycleId,
    refetchInterval: 30_000,
  });

  // ── Liquidation Boundaries (Manual) — state + query + mutation ──
  const { data: settingsData } = useQuery({
    queryKey: ['bot-settings'],
    queryFn: () => api.get<{ settings: Record<string, any> }>('/api/bot/settings'),
    refetchInterval: 60_000,
  });
  const s = settingsData?.settings || {};
  const [manualCallFloor, setManualCallFloor] = useState(0);
  const [manualPutCeiling, setManualPutCeiling] = useState(0);
  const [liqFormError, setLiqFormError] = useState<string | null>(null);
  const [liqFormOk, setLiqFormOk] = useState<string | null>(null);
  useEffect(() => {
    const rawFloor = Number(s.manual_call_floor) || 0;
    const rawCeiling = Number(s.manual_put_ceiling) || 0;
    setManualCallFloor(rawFloor);
    setManualPutCeiling(rawCeiling);
  }, [s.manual_call_floor, s.manual_put_ceiling]);
  const saveLiqBoundaries = useMutation({
    mutationFn: () => {
      const updatedAt = (manualCallFloor > 0 && manualPutCeiling > 0)
        ? new Date().toISOString()
        : null;
      return api.put('/api/bot/settings', {
        settings: {
          manual_call_floor: manualCallFloor,
          manual_put_ceiling: manualPutCeiling,
          manual_liq_updated_at: updatedAt,
        },
      });
    },
    onSuccess: () => {
      setLiqFormError(null);
      setLiqFormOk('Tersimpan.');
      qc.invalidateQueries({ queryKey: ['bot-settings'] });
      setTimeout(() => setLiqFormOk(null), 3000);
    },
    onError: (err: Error) => {
      setLiqFormOk(null);
      setLiqFormError(err.message || 'Gagal menyimpan');
    },
  });

  // Derived freshness
  const floor = Number(s.manual_call_floor) || 0;
  const ceiling = Number(s.manual_put_ceiling) || 0;
  const updatedAt = s.manual_liq_updated_at as string | null;
  const hasBoundaries = floor > 0 && ceiling > 0;
  const STALE_TTL_MS = 24 * 60 * 60 * 1000;
  const ageMs = updatedAt ? Date.now() - new Date(updatedAt).getTime() : null;
  const isFresh = hasBoundaries && ageMs !== null && ageMs < STALE_TTL_MS;
  const ageHours = ageMs !== null ? Math.round(ageMs / (1000 * 60 * 60) * 10) / 10 : null;
  const liqStatus: 'fresh' | 'stale' | 'unset' = !hasBoundaries ? 'unset' : !isFresh ? 'stale' : 'fresh';
  const liqStatusLabel =
    liqStatus === 'fresh' ? '✅ Fresh' :
    liqStatus === 'stale' ? '⚠ Stale (>24 jam)' : '⊘ Belum di-set';
  const liqStatusColor =
    liqStatus === 'fresh' ? 'var(--cin-emerald)' :
    liqStatus === 'stale' ? 'var(--cin-amber)' : 'var(--cin-text-faint)';

  const runBot = useMutation({
    mutationFn: () => api.post<any>('/api/bot/run', { narrate: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['candidates'] });
      qc.invalidateQueries({ queryKey: ['bot-activity'] });
      qc.invalidateQueries({ queryKey: ['context'] });
    },
    onError: () => qc.invalidateQueries({ queryKey: ['bot-activity'] }),
  });

  const candidates = candidatesData?.candidates || [];
  const stats = candidatesData?.stats;
  const isPass = (c: Candidate) => c.reject_reason == null || String(c.reject_reason).trim() === '';
  const passed = candidates.filter(isPass);
  const rejected = candidates.filter((c) => !isPass(c));
  const top = [...passed].sort((a, b) => Number(a.soft_score) - Number(b.soft_score)).slice(0, 5);

  const runResult = (runBot.data || activity?.result) as
    | {
        summary?: string;
        candidates_total?: number;
        candidates_passed?: number;
        candidates_rejected?: number;
        duration_ms?: number;
        warnings?: string[];
      }
    | undefined;

  // Prefer stats DB cycle terakhir (tahan refresh); activity hanya bonus
  const displayPassed = stats?.passed ?? runResult?.candidates_passed ?? passed.length;
  const displayRejected = stats?.rejected ?? runResult?.candidates_rejected ?? rejected.length;
  const displayTotal = stats?.total ?? runResult?.candidates_total ?? candidates.length;

  const running = runBot.isPending || !!activity?.running;

  // Alasan tolak paling sering (dari batch terbaru)
  const rejectWhy: Record<string, number> = {};
  for (const c of rejected.slice(0, 200)) {
    const r = String(c.reject_reason || '');
    const key = r.startsWith('macro')
      ? 'Macro (kalender)'
      : r.startsWith('liquidation')
        ? 'Likuidasi'
        : r.includes('min_distance') || r.includes('distance')
          ? 'Jarak strike'
          : r.startsWith('soft_score')
            ? 'Soft score tinggi'
            : 'Lainnya';
    rejectWhy[key] = (rejectWhy[key] || 0) + 1;
  }
  const topReject = Object.entries(rejectWhy).sort((a, b) => b[1] - a[1])[0];

  return (
    <div className="cin-bg">
      <div className="dash-header">
        <div>
          <div className="dash-header__title">{desktop.dashboard.title}</div>
          <div className="dash-header__sub">{desktop.dashboard.sub}</div>
        </div>
        <button
          className="glass-btn glass-btn--primary"
          onClick={() => runBot.mutate()}
          disabled={running}
        >
          <span className="glass-btn-inner">
            {running ? desktop.dashboard.ctaRunning : `▶ ${desktop.dashboard.cta}`}
          </span>
        </button>
      </div>

      {/* ===== HERO: apa yang terjadi ===== */}
      <div className="glass-card glass-card--md" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, justifyContent: 'space-between' }}>
          <div style={{ flex: '1 1 280px' }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--cin-text-faint)' }}>
              Status
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--cin-text)', marginTop: 6 }}>
              {running
                ? desktop.dashboard.statusRunning
                : displayPassed > 0
                  ? desktop.dashboard.statusHasPass(displayPassed)
                  : displayTotal > 0
                    ? desktop.dashboard.statusNone
                    : desktop.dashboard.statusEmpty}
            </div>
            <div style={{ fontSize: 13, color: 'var(--cin-text-muted)', marginTop: 8, lineHeight: 1.5 }}>
              {running
                ? `Lihat log di bawah. ${brand.company} · rekomendasi Nevi.`
                : runResult?.summary
                  || (displayTotal
                    ? `Cycle terakhir: ${displayPassed} lolos · ${displayRejected} ditolak · ${displayTotal} dianalisis. Detail di Rekomendasi Nevi.`
                    : desktop.dashboard.hintEmpty)}
            </div>
            {topReject && !running && displayRejected > 0 && (
              <div style={{ fontSize: 12, color: 'var(--cin-amber)', marginTop: 10 }}>
                Alasan tolak terbanyak: <strong>{topReject[0]}</strong> ({topReject[1]}x di sample)
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center', minWidth: 72 }}>
              <div style={{ fontSize: 10, color: 'var(--cin-text-faint)' }}>LOLOS</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--cin-emerald)' }}>
                {displayPassed}
              </div>
            </div>
            <div style={{ textAlign: 'center', minWidth: 72 }}>
              <div style={{ fontSize: 10, color: 'var(--cin-text-faint)' }}>DITOLAK</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--cin-rose)' }}>
                {displayRejected}
              </div>
            </div>
            <div style={{ textAlign: 'center', minWidth: 72 }}>
              <div style={{ fontSize: 10, color: 'var(--cin-text-faint)' }}>SCAN</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--cin-text)' }}>
                {displayTotal}
              </div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
          <Link to="/candidates" className="glass-btn" style={{ padding: '8px 14px', fontSize: 12 }}>
            {desktop.dashboard.linkCandidates}
          </Link>
          <Link to="/kalender" style={{ fontSize: 12, color: 'var(--cin-accent)', alignSelf: 'center' }}>Kalender →</Link>
          <Link to="/sentimen" style={{ fontSize: 12, color: 'var(--cin-accent)', alignSelf: 'center' }}>Sentimen →</Link>
          <Link to="/market" style={{ fontSize: 12, color: 'var(--cin-accent)', alignSelf: 'center' }}>Market →</Link>
          <Link to="/likuidasi" style={{ fontSize: 12, color: 'var(--cin-accent)', alignSelf: 'center' }}>Likuidasi →</Link>
        </div>
      </div>

      {/* ===== Gate Breakdown (cycle summary) ===== */}
      {cycleSummary && !running && (
        <div className="glass-card glass-card--sm" style={{ marginBottom: 16, padding: '14px 18px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--cin-text-faint)', textTransform: 'uppercase', marginBottom: 10 }}>
            Gate breakdown · cycle {cycleSummary.cycle_id?.slice(0, 8)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
            <div className="g-card" style={{ padding: '10px 14', textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'var(--gl-text-faint)' }}>📅 Macro</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--cin-emerald)' }}>{cycleSummary.total - cycleSummary.gate1_fail}</div>
              <div style={{ fontSize: 11, color: 'var(--cin-rose)' }}>✕ {cycleSummary.gate1_fail}</div>
            </div>
            <div className="g-card" style={{ padding: '10px 14', textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'var(--gl-text-faint)' }}>🔥 Likuidasi</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--cin-emerald)' }}>{cycleSummary.total - cycleSummary.gate2_fail}</div>
              <div style={{ fontSize: 11, color: 'var(--cin-rose)' }}>✕ {cycleSummary.gate2_fail}</div>
            </div>
            <div className="g-card" style={{ padding: '10px 14', textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'var(--gl-text-faint)' }}>📏 Jarak</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--cin-emerald)' }}>{cycleSummary.total - cycleSummary.gate3_fail}</div>
              <div style={{ fontSize: 11, color: 'var(--cin-rose)' }}>✕ {cycleSummary.gate3_fail}</div>
            </div>
            <div className="g-card" style={{ padding: '10px 14', textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'var(--gl-text-faint)' }}>📊 Score</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--cin-emerald)' }}>{cycleSummary.total - cycleSummary.gate1_fail - cycleSummary.gate2_fail - cycleSummary.gate3_fail - cycleSummary.soft_fail}</div>
              <div style={{ fontSize: 11, color: 'var(--cin-rose)' }}>✕ {cycleSummary.soft_fail}</div>
            </div>
            <div className="g-card" style={{ padding: '10px 14', textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'var(--gl-text-faint)' }}>Rata² Score</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--gl-text)' }}>{cycleSummary.avg_score?.toFixed(1) || '—'}</div>
              <div style={{ fontSize: 11, color: 'var(--gl-text-faint)' }}>min {cycleSummary.min_score?.toFixed(0) || '—'} · max {cycleSummary.max_score?.toFixed(0) || '—'}</div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Snapshot market (pointer, bukan detail) ===== */}
      <div className="glass-card glass-card--sm" style={{ marginBottom: 16, padding: '14px 18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--cin-text-faint)', textTransform: 'uppercase' }}>Market snapshot</span>
          <Link to="/market" style={{ fontSize: 11, color: 'var(--cin-accent)' }}>Detail Market →</Link>
        </div>
        <div className="ticker-bar" style={{ gap: 16 }}>
          <div className="ticker-bar__item">
            <span className="ticker-bar__label">BTC</span>
            <span className="ticker-bar__value">{fmt.number(ctxBtc?.spot_price, 0)}</span>
          </div>
          <div className="ticker-bar__sep" />
          <div className="ticker-bar__item">
            <span className="ticker-bar__label">DVOL</span>
            <span className="ticker-bar__value">{fmt.percent(ctxBtc?.dvol, 1)}</span>
          </div>
          <div className="ticker-bar__sep" />
          <div className="ticker-bar__item">
            <span className="ticker-bar__label">F&amp;G</span>
            <span className="ticker-bar__value">
              {ctxBtc?.fear_greed != null ? `${ctxBtc.fear_greed}` : '—'}
            </span>
          </div>
          <div className="ticker-bar__sep" />
          <div className="ticker-bar__item">
            <span className="ticker-bar__label">Fund</span>
            <span className="ticker-bar__value">
              {fmt.percent(ctxBtc?.funding_rate != null ? Number(ctxBtc.funding_rate) * 100 : null, 3)}
            </span>
          </div>
        </div>
      </div>

      {/* ===== Liquidation Boundaries (Manual) — Gate 2 ===== */}
      <div className="glass-card glass-card--sm" style={{ marginBottom: 16, padding: '14px 18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--cin-text-faint)', textTransform: 'uppercase' }}>
            🔥 Liquidation Boundaries (Manual) · Gate 2
          </span>
          <span style={{ fontSize: 11, color: liqStatusColor, fontWeight: 600 }}>
            {liqStatusLabel}
          </span>
        </div>
        <p style={{ fontSize: 11, color: 'var(--cin-text-muted)', margin: '0 0 10px', lineHeight: 1.4 }}>
          Screenshot heatmap likuidasi, lalu masukkan batas bawah/atas.
          Data otomatis expire setelah 24 jam — setelah itu semua kandidat ditolak sampai kamu update ulang.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
          <div>
            <label style={{ fontSize: 10, color: 'var(--cin-text-faint)', display: 'block', marginBottom: 4 }}>
              CALL (Sell High) — Safe Floor
            </label>
            <input
              type="number" min={0} step={100}
              value={manualCallFloor}
              onChange={(e) => setManualCallFloor(Number(e.target.value))}
              style={{
                width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--cin-border)',
                background: 'var(--bg-well, #0f1311)', color: 'var(--cin-text)', fontSize: 14,
                fontFamily: 'var(--font-mono)',
                boxSizing: 'border-box',
              }}
            />
            <span style={{ fontSize: 10, color: 'var(--cin-text-faint)', marginTop: 2, display: 'block' }}>
              Strike CALL harus di ATAS harga ini
            </span>
          </div>
          <div>
            <label style={{ fontSize: 10, color: 'var(--cin-text-faint)', display: 'block', marginBottom: 4 }}>
              PUT (Buy Low) — Safe Ceiling
            </label>
            <input
              type="number" min={0} step={100}
              value={manualPutCeiling}
              onChange={(e) => setManualPutCeiling(Number(e.target.value))}
              style={{
                width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--cin-border)',
                background: 'var(--bg-well, #0f1311)', color: 'var(--cin-text)', fontSize: 14,
                fontFamily: 'var(--font-mono)',
                boxSizing: 'border-box',
              }}
            />
            <span style={{ fontSize: 10, color: 'var(--cin-text-faint)', marginTop: 2, display: 'block' }}>
              Strike PUT harus di BAWAH harga ini
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={() => saveLiqBoundaries.mutate()}
            disabled={saveLiqBoundaries.isPending}
            className="glass-btn glass-btn--primary"
            style={{ padding: '6px 14px', fontSize: 12 }}
          >
            <span className="glass-btn-inner">
              {saveLiqBoundaries.isPending ? 'Menyimpan…' : '💾 Simpan Boundaries'}
            </span>
          </button>
          {liqFormOk && <span style={{ fontSize: 11, color: 'var(--cin-emerald)' }}>{liqFormOk}</span>}
          {liqFormError && <span style={{ fontSize: 11, color: 'var(--cin-rose)' }}>{liqFormError}</span>}
          <span style={{ fontSize: 11, color: 'var(--cin-text-faint)' }}>
            {updatedAt
              ? `last update: ${new Date(updatedAt).toLocaleString('id-ID')} (${ageHours} jam lalu)`
              : 'belum pernah di-set'}
          </span>
          <Link to="/likuidasi" style={{ fontSize: 11, color: 'var(--cin-accent)' }}>Detail →</Link>
        </div>
      </div>

      <div className="cin-grid-2">
        {/* Top rekomendasi */}
        <div className="glass-card glass-card--md">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Rekomendasi</span>
            <Link to="/candidates" style={{ fontSize: 12, color: 'var(--cin-accent)' }}>Semua →</Link>
          </div>
          {candLoading ? (
            <div className="empty-state">Memuat…</div>
          ) : top.length === 0 ? (
            <div className="empty-state">
              {desktop.dashboard.emptyRec}
            </div>
          ) : (
            <table className="glass-table">
              <thead>
                <tr>
                  <th>Produk</th>
                  <th>Strike</th>
                  <th>APR</th>
                  <th>Score</th>
                  <th>Settle</th>
                </tr>
              </thead>
              <tbody>
                {top.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <strong>{c.underlying}</strong>{' '}
                      <span style={{ fontSize: 10, color: 'var(--cin-text-muted)' }}>
                        {getOptionIcon(c.option_type)} {getOptionLabel(c.option_type)}
                      </span>
                    </td>
                    <td>{fmt.number(c.strike_price, 0)}</td>
                    <td style={{ color: 'var(--cin-emerald)' }}>{fmt.percent(c.apr, 2)}</td>
                    <td>{fmt.score(c.soft_score)}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{fmt.countdown(c.settle_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p style={{ fontSize: 11, color: 'var(--cin-text-faint)', marginTop: 12, marginBottom: 0, lineHeight: 1.45 }}>
            Score makin <strong>rendah</strong> makin aman (0–100). APR tinggi bukan prioritas.
          </p>
        </div>

        {/* Log ringkas */}
        <div className="glass-card glass-card--md">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              Log run {activity?.running ? <span style={{ color: 'var(--accent-primary)' }}>· live</span> : null}
            </span>
            <span style={{ fontSize: 11, color: 'var(--cin-text-faint)', fontFamily: 'var(--font-mono)' }}>
              {activity?.cycle_id?.slice(0, 8) || '—'}
            </span>
          </div>
          <div style={{
            maxHeight: 220, overflowY: 'auto', fontFamily: 'var(--font-mono)', fontSize: 11,
            background: 'var(--bg-well, #0f1311)', borderRadius: 10, padding: 10,
          }}>
            {(activity?.steps?.length ?? 0) === 0 && (
              <div style={{ color: 'var(--cin-text-faint)' }}>{desktop.dashboard.emptyLog}</div>
            )}
            {(activity?.steps || []).slice(-25).map((s, i) => (
              <div key={i} style={{
                padding: '3px 0',
                color: s.level === 'error' ? 'var(--cin-rose)' : s.level === 'warn' ? 'var(--cin-amber)' : s.level === 'ok' ? 'var(--cin-emerald)' : 'var(--cin-text-muted)',
              }}>
                <span style={{ opacity: 0.5 }}>{s.at ? new Date(s.at).toLocaleTimeString('id-ID') : ''} </span>
                {s.message}
              </div>
            ))}
          </div>
          {runBot.isError && (
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--cin-rose)' }}>
              {(runBot.error as Error)?.message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
